import { getSignInPolicyConfirmParams } from "../identityPolicyConfirmations";

import { SectionShell } from "./SectionShell";

import HelpTooltip from "@/components/ui/HelpTooltip";
import { Switch } from "@/components/switch";
import { ToastType } from "@/enums";
import { useConfirm } from "@/context/confirm-context";
import {
  IdentitySettings,
  useUpdateIdentitySettings,
} from "@/queries/identity/useIdentitySettings";
import { formatMutationError } from "@/utils/errors";
import { displayToast } from "@/utils/toast";

interface Props {
  organizationId: number;
  settings: IdentitySettings;
  onError: (msg: string | null) => void;
}

const ToggleRow = ({
  title,
  description,
  helpTooltip,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description: string;
  helpTooltip?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
}) => (
  <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
    <div className="flex-1">
      <p className="flex items-center text-sm font-medium text-gray-900">
        {title}
        {helpTooltip && <HelpTooltip content={helpTooltip} />}
      </p>
      <p className="text-xs text-gray-600">{description}</p>
    </div>
    <Switch checked={checked} onChange={onChange} disabled={disabled} size="sm" />
  </div>
);

const REQUIRE_SSO_MFA_HELP =
  "For regular members, MFA is enforced by your SSO provider (for example Azure Entra or Okta), not in PromptLayer. Organization Owners with emergency access must enroll MFA in Account Settings before Require SSO can be enabled. That MFA applies only to emergency email/password login, not SSO sign-in.";

/**
 * Sign-in policy toggles: `sso_enabled`, `require_sso` (also bumps
 * `identity_policy_version` so existing JWTs invalidate on next
 * request), and `jit_provisioning_enabled`.
 */
export const SignInPolicySection = ({ organizationId, settings, onError }: Props) => {
  const updateSettings = useUpdateIdentitySettings(organizationId);
  const { confirm } = useConfirm();

  const updateField = <K extends "sso_enabled" | "require_sso" | "jit_provisioning_enabled">(
    key: K,
    value: boolean,
  ) => {
    onError(null);
    updateSettings.mutate(
      { [key]: value },
      {
        onSuccess: () => {
          if (key === "require_sso") {
            displayToast(
              value
                ? "Require SSO enabled. Existing sessions for non-emergency-access users will be signed out on next request."
                : "Require SSO disabled. Email/password login is available again.",
              ToastType.success,
            );
          } else {
            displayToast("Setting saved.", ToastType.success);
          }
        },
        onError: error => onError(formatMutationError(error, "Failed to update sign-in policy.")),
      },
    );
  };

  const confirmAndUpdateField = async <
    K extends "sso_enabled" | "require_sso" | "jit_provisioning_enabled",
  >(
    key: K,
    value: boolean,
  ) => {
    const confirmed = await confirm(getSignInPolicyConfirmParams(key, value));
    if (confirmed) {
      updateField(key, value);
    }
  };

  return (
    <SectionShell
      title="Sign-in policy"
      description="Control how org members authenticate to PromptLayer."
    >
      <ToggleRow
        title="SSO enabled"
        description="Allow members to sign in via the configured SAML/OIDC connection."
        checked={settings.sso_enabled}
        onChange={v => confirmAndUpdateField("sso_enabled", v)}
        disabled={updateSettings.isPending}
      />
      <ToggleRow
        title="Require SSO"
        description="Block email/password sign-in for this org. Organization Owners with emergency access keep email/password login."
        helpTooltip={REQUIRE_SSO_MFA_HELP}
        checked={settings.require_sso}
        onChange={v => confirmAndUpdateField("require_sso", v)}
        disabled={updateSettings.isPending}
      />
      <ToggleRow
        title="JIT provisioning"
        description="Auto-create PromptLayer accounts on first successful SSO login. Allowed email domains are configured on the WorkOS SSO connection."
        checked={settings.jit_provisioning_enabled}
        onChange={v => confirmAndUpdateField("jit_provisioning_enabled", v)}
        disabled={updateSettings.isPending}
      />
    </SectionShell>
  );
};
